import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { FiPlus, FiEdit2, FiTrash2, FiChevronDown, FiChevronUp, FiShare2 } from 'react-icons/fi';
import {
  fetchBubbleTeaOptionCategories,
  createOptionItem,
  updateOptionItem,
  deleteOptionItem,
  toggleOptionItem,
  updateOptionCategory,
  createOptionCategory,
  deleteOptionCategory,
  bulkApplyOptionToCategory,
  BubbleTeaOptionCategory,
  BubbleTeaOptionItem,
} from '../../api/adminBubbleTeaOptions';
import axiosInstance from '../../api/axios';
import { formatCurrency } from '../../utils/currency';
import { getErrorDetail } from '../../utils/errorDetail';
import { trimShortCode } from '../../utils/trimShortCode';

// ─── Styled Components (matching AdminCategories / AdminProducts patterns) ────

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #333;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const IconButton = styled.button<{ variant?: 'edit' | 'delete' }>`
  padding: 8px;
  background: ${props => props.variant === 'delete' ? '#fee2e2' : '#ede9fe'};
  color: ${props => props.variant === 'delete' ? '#dc2626' : '#667eea'};
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${props => props.variant === 'delete' ? '#fecaca' : '#ddd6fe'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  background: #f3f4f6;
  color: #6b7280;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #e5e7eb;
  }
`;

// ─── Section (collapsible card) ───────────────────────────────────────────────

const Section = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
  overflow: hidden;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 24px;
  cursor: pointer;
  user-select: none;
  transition: background 0.15s ease;

  &:hover {
    background: #f9fafb;
  }
`;

const SectionTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const ItemCount = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  background: #f3f4f6;
  padding: 2px 8px;
  border-radius: 10px;
`;

const SectionHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ChevronIcon = styled.span`
  color: #6b7280;
  display: flex;
  align-items: center;
`;

const SectionBody = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'block' : 'none'};
  border-top: 1px solid #e5e7eb;
`;

// ─── Table ────────────────────────────────────────────────────────────────────

const TableWrapper = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
`;

const Td = styled.td`
  padding: 12px 16px;
  font-size: 14px;
  color: #333;
  border-bottom: 1px solid #f3f4f6;
  vertical-align: middle;
`;

const Tr = styled.tr`
  transition: background 0.1s ease;

  &:hover {
    background: #fafafa;
  }

  &:last-child td {
    border-bottom: none;
  }
`;

const ActionCell = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

// ─── Badges ───────────────────────────────────────────────────────────────────

const DefaultBadge = styled.span`
  display: inline-block;
  padding: 3px 8px;
  background: #d1fae5;
  color: #065f46;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
`;

const PriceBadge = styled.span`
  display: inline-block;
  padding: 3px 8px;
  background: #ede9fe;
  color: #5b21b6;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
`;

// ─── Toggle switch ────────────────────────────────────────────────────────────

const ToggleLabel = styled.label`
  position: relative;
  display: inline-block;
  width: 38px;
  height: 22px;
  cursor: pointer;

  input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  span {
    position: absolute;
    inset: 0;
    background: #e5e7eb;
    border-radius: 22px;
    transition: background 0.2s ease;

    &::before {
      content: '';
      position: absolute;
      width: 16px;
      height: 16px;
      left: 3px;
      top: 3px;
      background: white;
      border-radius: 50%;
      transition: transform 0.2s ease;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    }
  }

  input:checked + span {
    background: #667eea;

    &::before {
      transform: translateX(16px);
    }
  }

  input:disabled + span {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// ─── Empty state ──────────────────────────────────────────────────────────────

const EmptyRow = styled.tr``;

const EmptyCell = styled.td`
  padding: 32px 16px;
  text-align: center;
  color: #9ca3af;
  font-size: 14px;
`;

// ─── Modal ────────────────────────────────────────────────────────────────────

const Modal = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  max-width: 480px;
  width: 90%;
  padding: 24px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #333;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: #6b7280;
  cursor: pointer;
  line-height: 1;

  &:hover {
    color: #333;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

// Places an EN FormGroup next to its paired ZH (Chinese) FormGroup, e.g. for
// Name / Name (Chinese). Mirrors AdminCategories.tsx / AdminProducts.tsx's
// FormRow (task-025-s4-3/s4-4), applied here for story-004-4.
const FormRow = styled.div`
  display: flex;
  gap: 16px;

  ${FormGroup} {
    flex: 1;
    min-width: 0;
  }
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 6px;
`;

const Input = styled.input`
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const Select = styled.select`
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 14px;
  background: white;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #333;
  cursor: pointer;
`;

// Single-select/Multi-select radio group for the Add Category modal's
// Selection Mode field (task-028-s5-1).
const RadioGroup = styled.div`
  display: flex;
  gap: 16px;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #333;
  cursor: pointer;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
`;

const SubmitButton = styled(Button)`
  margin: 0;
`;

// ─── Loading / Error ──────────────────────────────────────────────────────────

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
`;

const Spinner = styled.div`
  border: 3px solid #e5e7eb;
  border-top: 3px solid #667eea;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  background: #fee2e2;
  color: #991b1b;
  border-radius: 8px;
  padding: 16px 20px;
  font-size: 14px;
  margin-bottom: 20px;
`;

// Success feedback for the Apply-to-category modal (task-028-s6-1), mirroring
// AdminLoyaltyPoints.tsx's success Toast color scheme.
const SuccessMessage = styled.div`
  background: #d1fae5;
  color: #065f46;
  border-radius: 8px;
  padding: 16px 20px;
  font-size: 14px;
  margin-bottom: 20px;
`;

// Dismissible advisory banner (task-028-s8-fix-4, broadened in
// task-028-s8-fix2-3): informs an admin that a category/item they just
// created may not yet be available on any product. For categories this is
// always true (a brand-new category always starts with zero items). For
// items, the backend seeds availability from the UNION of sibling items'
// CURRENT bubble_tea_menu_item_options rows (see add_item_to_category),
// which can be empty for the 1st, 2nd, or later item added -- so the notice
// is shown unconditionally after any item creation rather than gated on
// "was this literally the first item," which under-reported the cases where
// zero rows were actually seeded.
const InfoMessage = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  background: rgba(217, 119, 6, 0.12);
  color: #92400e;
  border-radius: 8px;
  padding: 12px 16px;
  font-size: 13px;
  line-height: 1.5;
  margin-bottom: 16px;
`;

const InfoDismissButton = styled.button`
  background: none;
  border: none;
  font-size: 18px;
  line-height: 1;
  color: #92400e;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;

  &:hover {
    opacity: 0.7;
  }
`;

// Dismissible error-toned counterpart to InfoMessage/InfoDismissButton above
// (task-034-impl-1): same flex layout and dismiss-button shape, but keeps the
// existing red ErrorMessage color scheme (#991b1b on #fee2e2) instead of the
// amber info palette, since these banners report failures (delete-category /
// item-delete), not informational notices.
const ErrorBanner = styled(ErrorMessage)`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
`;

const ErrorDismissButton = styled(InfoDismissButton)`
  color: #991b1b;
`;

// ─── Types ────────────────────────────────────────────────────────────────────

interface ItemFormState {
  name: string;
  name_zh: string;
  extra_price: number;
  is_default: boolean;
  active: boolean;
  sort_order: number;
  option_code: string;
  combo_qr_unavailable: boolean;
}

const BLANK_FORM: ItemFormState = {
  name: '',
  name_zh: '',
  extra_price: 0,
  is_default: false,
  active: true,
  sort_order: 0,
  option_code: '',
  combo_qr_unavailable: false,
};

// ─── Category name form state (edit-only: option types are a fixed set of 5,
// so there is no "create category" flow — see CategorySection below) ─────────

interface CategoryFormState {
  name: string;
  name_zh: string;
  // task-032-impl-6: not user-editable here when the category's own
  // (non-editable-in-this-modal) selection_mode is 'multiple' -- see
  // editTriggersQrVariantDisabled in OptionSection below.
  triggers_qr_variant: boolean;
}

const BLANK_CATEGORY_FORM: CategoryFormState = {
  name: '',
  name_zh: '',
  triggers_qr_variant: false,
};

// ─── Add Category form state (task-028-s5-1: admins can now create brand-new
// option categories with a free-text option type, not just the original 5
// pre-seeded ones) ──────────────────────────────────────────────────────────

interface AddCategoryFormState {
  name: string;
  name_zh: string;
  option_type: string;
  selection_mode: 'single' | 'multiple';
  // task-032-impl-6: disabled (and forced back to false, see the
  // Selection Mode radio onChange handlers below) whenever selection_mode
  // is 'multiple' -- mirrors the backend's 422 rejection of that combination.
  triggers_qr_variant: boolean;
  sort_order: number;
}

const BLANK_ADD_CATEGORY_FORM: AddCategoryFormState = {
  name: '',
  name_zh: '',
  option_type: '',
  selection_mode: 'single',
  triggers_qr_variant: false,
  sort_order: 0,
};

// ─── Apply-to-category form state (task-028-s6-1: bulk-enable/disable one
// option item across every product sharing a menu_category) ───────────────

interface ApplyToCategoryFormState {
  menu_category: string;
  available: boolean;
}

const BLANK_APPLY_FORM: ApplyToCategoryFormState = {
  menu_category: '',
  available: true,
};

// menu_category is the AdminQBCategories.tsx product-grouping setting (an
// AppSetting-JSON list, NOT the BubbleTeaOptionCategory table this file
// otherwise manages) -- reuses the exact GET /admin/settings/qb-categories
// fetch/shape/sort-by-display_order pattern already established in
// AdminQBCategories.tsx, duplicated locally here rather than imported since
// AdminQBCategories.tsx exports no reusable function/module for it.
interface QBCategory {
  name: string;
  name_zh?: string;
  display_order: number;
}

const fetchQBCategories = async (): Promise<QBCategory[]> => {
  const response = await axiosInstance.get('/admin/settings/qb-categories');
  const data = response.data;
  const cats: QBCategory[] = Array.isArray(data.categories) ? data.categories : [];
  return [...cats].sort((a, b) => a.display_order - b.display_order);
};

// ─── Sub-component: one collapsible section ───────────────────────────────────

interface OptionSectionProps {
  category: BubbleTeaOptionCategory;
}

// Every section always shows the Extra Price column/field (task-028-s5-1) --
// there is no longer a fixed 5-type list to key a per-type showPrice flag
// off of, since admins can create arbitrary option_type categories.
const OptionSection: React.FC<OptionSectionProps> = ({ category }) => {
  const { t, i18n } = useTranslation();
  const type = category.option_type;
  // Section label is derived from the category's own name/name_zh
  // (task-028-s5-1), not an i18n-keyed lookup -- this is what makes
  // brand-new, non-preseeded categories render correctly with no code
  // change, while still switching language like every other translated
  // string on this page (falls back to the English name if no Chinese
  // translation was given).
  const label = (i18n.language.startsWith('zh') && category.name_zh) ? category.name_zh : category.name;
  const showPrice = true;
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<BubbleTeaOptionItem | null>(null);
  const [form, setForm] = useState<ItemFormState>(BLANK_FORM);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [categoryForm, setCategoryForm] = useState<CategoryFormState>(BLANK_CATEGORY_FORM);
  const [applyItem, setApplyItem] = useState<BubbleTeaOptionItem | null>(null);
  const [applyForm, setApplyForm] = useState<ApplyToCategoryFormState>(BLANK_APPLY_FORM);
  const [newItemNotice, setNewItemNotice] = useState<string | null>(null);
  // task-032 fix_review MEDIUM finding: surfaces the server-generated
  // combo_qr_warning string (see createMutation/updateMutation/
  // updateCategoryMutation's onSuccess below) whenever a rename or a
  // same-name-as-a-prior-sibling create may have orphaned or silently
  // reused an existing drink-machine combo QR token. Plain English from the
  // backend, rendered verbatim -- not i18n-keyed, matching this file's
  // existing convention for other backend-generated messages.
  const [comboQrWarning, setComboQrWarning] = useState<string | null>(null);
  const [deleteCategoryError, setDeleteCategoryError] = useState<string | null>(null);
  // task-034-impl-1: kept separate from deleteCategoryError so a failed
  // item-level delete never shows category-delete copy (or vice versa) --
  // see OptionSection's two dismissible banners below.
  const [itemDeleteError, setItemDeleteError] = useState<string | null>(null);
  const itemDeleteErrorItemIdRef = useRef<number | null>(null);
  // task-034-fix-2: tracks the window between deleteCategoryMutation's own network call
  // resolving (isPending -> false) and the invalidate()-triggered categories refetch actually
  // settling. deleteCategoryMutation.isPending alone is NOT sufficient to gate item-level
  // controls -- it flips false before onSuccess (and therefore before invalidate()) even runs.
  const [categoryDeleteRefetchPending, setCategoryDeleteRefetchPending] = useState(false);
  const sessionIdRef = useRef(0);
  const applySessionIdRef = useRef(0);

  // menu_category options come from the qb_categories AppSetting list. The
  // fetch/endpoint here is intentionally duplicated from AdminQBCategories.tsx,
  // but under its own namespaced cache key -- AdminQBCategories.tsx owns its
  // own separate react-query cache key and that key must NOT be reused here.
  // NOT from BubbleTeaOptionCategory -- only fetched once an Apply modal is
  // actually opened.
  const { data: qbCategoriesRaw, isLoading: qbCategoriesLoading, isError: qbCategoriesError } = useQuery({
    queryKey: ['adminBubbleTeaOptions', 'qbCategories'],
    queryFn: fetchQBCategories,
    enabled: applyItem !== null,
  });
  const qbCategories = Array.isArray(qbCategoriesRaw) ? qbCategoriesRaw : [];

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['bubbleTeaOptionCategories'] });

  const createMutation = useMutation({
    mutationFn: ({ data }: { sessionId: number; data: Partial<ItemFormState> }) =>
      createOptionItem(category.id, data),
    onSuccess: (created, variables) => {
      invalidate();
      setDeleteCategoryError(null);
      setItemDeleteError(null);
      itemDeleteErrorItemIdRef.current = null;
      if (variables.sessionId === sessionIdRef.current) {
        closeModal();
        // Always show the notice after creating an item (task-028-s8-fix2-3):
        // the backend seeds a brand-new item's availability from the UNION of
        // its siblings' CURRENT bubble_tea_menu_item_options rows, which can
        // be empty even for a 2nd/3rd item added to a category whose existing
        // item(s) were never applied to any product -- not just the literal
        // first-ever item. Computing the exact backend condition client-side
        // would need an extra API call per item-add just for this cosmetic
        // warning, so the copy below is deliberately generic ("may not be
        // available") rather than asserting zero availability is certain.
        setNewItemNotice(created.name);
      }
      if (created.combo_qr_warning) setComboQrWarning(created.combo_qr_warning);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<ItemFormState>; sessionId: number }) =>
      updateOptionItem(id, data),
    onSuccess: (data, variables) => {
      invalidate();
      setDeleteCategoryError(null);
      setItemDeleteError(null);
      itemDeleteErrorItemIdRef.current = null;
      if (variables.sessionId === sessionIdRef.current) closeModal();
      if (data.combo_qr_warning) setComboQrWarning(data.combo_qr_warning);
    },
    onError: (err: any, variables) => {
      itemDeleteErrorItemIdRef.current = variables.id;
      setItemDeleteError(getErrorDetail(err) || t('admin.bubbleTeaOptions.genericError'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteOptionItem(id),
    onSuccess: () => {
      invalidate();
      setDeleteCategoryError(null);
      setItemDeleteError(null);
      itemDeleteErrorItemIdRef.current = null;
    },
    onError: (err: any, id) => {
      itemDeleteErrorItemIdRef.current = id;
      setItemDeleteError(getErrorDetail(err) || t('admin.bubbleTeaOptions.genericError'));
    },
  });

  const toggleMutation = useMutation({
    mutationFn: (id: number) => toggleOptionItem(id),
    onSuccess: () => {
      invalidate();
      setDeleteCategoryError(null);
      setItemDeleteError(null);
      itemDeleteErrorItemIdRef.current = null;
    },
    onError: (err: any, id) => {
      itemDeleteErrorItemIdRef.current = id;
      setItemDeleteError(getErrorDetail(err) || t('admin.bubbleTeaOptions.genericError'));
    },
  });

  const updateCategoryMutation = useMutation({
    mutationFn: (data: Partial<CategoryFormState>) => updateOptionCategory(category.id, data),
    onSuccess: (data) => {
      invalidate();
      setDeleteCategoryError(null);
      setItemDeleteError(null);
      itemDeleteErrorItemIdRef.current = null;
      closeCategoryModal();
      if (data.combo_qr_warning) setComboQrWarning(data.combo_qr_warning);
    },
  });

  const deleteCategoryMutation = useMutation({
    mutationFn: (id: number) => deleteOptionCategory(id),
    onSuccess: async (_data, id) => {
      setDeleteCategoryError(null);
      setCategoryDeleteRefetchPending(true);
      // task-036-finding-2: invalidate() itself throwing is a distinct failure
      // mode from the stillPresent/error-status check below -- invalidateQueries
      // swallows the underlying queryFn's own rejection into the query's status
      // ('error', handled below), it does not normally reject its own returned
      // promise. A throw here means something unexpected happened before we can
      // even inspect that query state/data, so there is nothing reliable to gate
      // on -- reset categoryDeleteRefetchPending immediately in this branch
      // (rather than leaving controls stuck disabled with no recovery) while
      // still surfacing the same refetch-failure banner.
      try {
        await invalidate();
      } catch {
        setDeleteCategoryError(t('admin.bubbleTeaOptions.categoryDeleteRefetchError'));
        setCategoryDeleteRefetchPending(false);
        return;
      }
      const refetchedCategories = queryClient.getQueryData<BubbleTeaOptionCategory[]>(['bubbleTeaOptionCategories']);
      const stillPresent =
        Array.isArray(refetchedCategories) && refetchedCategories.some((c) => c.id === id);
      if (
        queryClient.getQueryState(['bubbleTeaOptionCategories'])?.status === 'error' ||
        stillPresent
      ) {
        setDeleteCategoryError(t('admin.bubbleTeaOptions.categoryDeleteRefetchError'));
        // Deliberately do NOT reset categoryDeleteRefetchPending here (unlike
        // the success branch below): the on-screen category list is known to
        // be stale/unreliable at this point, so the gated controls (Edit/
        // Delete/+Add and all item-row controls) must stay disabled for as
        // long as this banner is shown. The banner's own copy already directs
        // the admin to reload the page -- that reload is the recovery path,
        // not a soft reset here.
      } else {
        setCategoryDeleteRefetchPending(false);
      }
    },
    onError: (err: any) => {
      setDeleteCategoryError(getErrorDetail(err) || t('admin.bubbleTeaOptions.genericError'));
    },
  });

  const applyMutation = useMutation({
    mutationFn: ({ data }: { sessionId: number; data: ApplyToCategoryFormState }) =>
      bulkApplyOptionToCategory({
        option_item_id: applyItem!.id,
        menu_category: data.menu_category,
        available: data.available,
      }),
    onSuccess: () => {
      setDeleteCategoryError(null);
      setItemDeleteError(null);
      itemDeleteErrorItemIdRef.current = null;
    },
  });

  const openApplyModal = (item: BubbleTeaOptionItem) => {
    applySessionIdRef.current += 1;
    setApplyItem(item);
    setApplyForm(BLANK_APPLY_FORM);
    applyMutation.reset();
  };

  const closeApplyModal = () => {
    if (applyMutation.isPending) return;
    applySessionIdRef.current += 1;
    setApplyItem(null);
    setApplyForm(BLANK_APPLY_FORM);
    applyMutation.reset();
  };

  const handleApplySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    applyMutation.mutate({ sessionId: applySessionIdRef.current, data: applyForm });
  };

  // The Edit Category modal doesn't let admins change selection_mode (see
  // the modal's stale-comment history above CategoryFormState), so the
  // "effective" selection_mode for the disabled-checkbox check is simply
  // the category's own current value -- unlike the Add modal, where it's
  // whatever the admin has picked in-form.
  //
  // task-032 fix_review: a concurrent-PATCH race in update_category can
  // leave a row stuck in the invalid state (selection_mode='multiple' AND
  // triggers_qr_variant=true) despite the 422 guard on a single request
  // that tries to set both at once (see that endpoint's row-lock fix).
  // Gating this on "&& !categoryForm.triggers_qr_variant" keeps the
  // checkbox disabled for turning ON (can't create the invalid combination
  // via this modal), while still allowing an admin to turn OFF an
  // already-checked box on a category stuck in that state -- a real repair
  // path with no other UI recovery route.
  const editTriggersQrVariantDisabled =
    category.selection_mode === 'multiple' && !categoryForm.triggers_qr_variant;

  const openEditCategory = (e: React.MouseEvent) => {
    e.stopPropagation(); // prevent collapsing the section
    setCategoryForm({
      name: category.name,
      name_zh: category.name_zh ?? '',
      triggers_qr_variant: category.triggers_qr_variant ?? false,
    });
    setCategoryModalOpen(true);
  };

  const closeCategoryModal = () => {
    setCategoryModalOpen(false);
    setCategoryForm(BLANK_CATEGORY_FORM);
    updateCategoryMutation.reset();
  };

  const handleCategorySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateCategoryMutation.mutate(categoryForm);
  };

  const handleDeleteCategory = (e: React.MouseEvent) => {
    e.stopPropagation(); // prevent collapsing the section
    if (window.confirm(t('admin.bubbleTeaOptions.deleteCategoryConfirm'))) {
      setDeleteCategoryError(null);
      deleteCategoryMutation.mutate(category.id);
    }
  };

  const openAdd = (e: React.MouseEvent) => {
    sessionIdRef.current += 1;
    e.stopPropagation(); // prevent collapsing the section
    setEditingItem(null);
    setForm({ ...BLANK_FORM, sort_order: category.items.length });
    setModalOpen(true);
  };

  const openEdit = (item: BubbleTeaOptionItem) => {
    sessionIdRef.current += 1;
    setEditingItem(item);
    setForm({
      name: item.name,
      name_zh: item.name_zh ?? '',
      extra_price: item.extra_price,
      is_default: item.is_default,
      active: item.active,
      sort_order: item.sort_order,
      option_code: item.option_code ?? '',
      combo_qr_unavailable: item.combo_qr_unavailable ?? false,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingItem(null);
    setForm(BLANK_FORM);
    createMutation.reset();
    updateMutation.reset();
    if (editingItem && itemDeleteErrorItemIdRef.current === editingItem.id) {
      setItemDeleteError(null);
      itemDeleteErrorItemIdRef.current = null;
    }
    sessionIdRef.current += 1;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const sessionId = sessionIdRef.current;
    // Blank option_code is omitted so the server auto-generates it (create) or
    // leaves it unchanged (update); a non-blank value is sent trimmed.
    // combo_qr_unavailable only applies to triggers_qr_variant categories; omitted otherwise.
    const { option_code, combo_qr_unavailable, ...baseRest } = form;
    const rest = category.triggers_qr_variant ? { ...baseRest, combo_qr_unavailable } : baseRest;
    const trimmedCode = trimShortCode(option_code);
    const data: Partial<ItemFormState> = trimmedCode ? { ...rest, option_code: trimmedCode } : rest;
    if (editingItem) {
      updateMutation.mutate({ id: editingItem.id, data, sessionId });
    } else {
      createMutation.mutate({ data, sessionId });
    }
  };

  const handleDelete = (id: number) => {
    if (window.confirm(t('admin.bubbleTeaOptions.deleteConfirm'))) {
      setItemDeleteError(null);
      deleteMutation.mutate(id);
    }
  };

  const isMutating =
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteCategoryMutation.isPending ||
    categoryDeleteRefetchPending;

  const anyItemMutationPending =
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending ||
    toggleMutation.isPending ||
    applyMutation.isPending;

  const createStale = createMutation.isError && createMutation.variables?.sessionId === sessionIdRef.current;
  const updateStale = updateMutation.isError && updateMutation.variables?.sessionId === sessionIdRef.current;
  const applyIsCurrentSession = applyMutation.variables?.sessionId === applySessionIdRef.current;

  return (
    <>
      <Section>
        <SectionHeader onClick={() => setIsOpen(prev => !prev)}>
          <SectionTitleRow>
            <SectionTitle>{label}</SectionTitle>
            <ItemCount>
              {category.items.length === 1
                ? t('admin.bubbleTeaOptions.itemCountSingular', { count: category.items.length })
                : t('admin.bubbleTeaOptions.itemCountPlural', { count: category.items.length })}
            </ItemCount>
          </SectionTitleRow>
          <SectionHeaderRight>
            <IconButton variant="edit" title={t('admin.bubbleTeaOptions.editCategoryTooltip')} disabled={deleteCategoryMutation.isPending || anyItemMutationPending || categoryDeleteRefetchPending} onClick={openEditCategory}>
              <FiEdit2 size={14} />
            </IconButton>
            <IconButton
              variant="delete"
              title={t('admin.bubbleTeaOptions.deleteCategoryTooltip')}
              disabled={deleteCategoryMutation.isPending || anyItemMutationPending || categoryDeleteRefetchPending}
              onClick={handleDeleteCategory}
            >
              <FiTrash2 size={14} />
            </IconButton>
            <Button disabled={deleteCategoryMutation.isPending || anyItemMutationPending || categoryDeleteRefetchPending} onClick={openAdd}>
              <FiPlus size={14} />
              {t('admin.bubbleTeaOptions.addButton')}
            </Button>
            <ChevronIcon>
              {isOpen ? <FiChevronUp size={18} /> : <FiChevronDown size={18} />}
            </ChevronIcon>
          </SectionHeaderRight>
        </SectionHeader>

        <SectionBody isOpen={isOpen}>
          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <Th>{t('admin.bubbleTeaOptions.nameHeader')}</Th>
                  {showPrice && <Th>{t('admin.bubbleTeaOptions.extraPriceHeader')}</Th>}
                  <Th>{t('admin.bubbleTeaOptions.defaultHeader')}</Th>
                  <Th>{t('admin.bubbleTeaOptions.sortHeader')}</Th>
                  <Th>{t('admin.bubbleTeaOptions.activeHeader')}</Th>
                  <Th>{t('admin.bubbleTeaOptions.actionsHeader')}</Th>
                </tr>
              </thead>
              <tbody>
                {category.items.length === 0 ? (
                  <EmptyRow>
                    <EmptyCell colSpan={showPrice ? 6 : 5}>
                      {t('admin.bubbleTeaOptions.emptyItems')}
                    </EmptyCell>
                  </EmptyRow>
                ) : (
                  [...category.items]
                    .sort((a, b) => a.sort_order - b.sort_order)
                    .map(item => (
                      <Tr key={item.id}>
                        <Td>{item.name}</Td>
                        {showPrice && (
                          <Td>
                            {item.extra_price > 0
                              ? <PriceBadge>+{formatCurrency(item.extra_price)}</PriceBadge>
                              : <span style={{ color: '#9ca3af' }}>—</span>
                            }
                          </Td>
                        )}
                        <Td>
                          {item.is_default && <DefaultBadge>{t('admin.bubbleTeaOptions.defaultBadge')}</DefaultBadge>}
                        </Td>
                        <Td style={{ color: '#6b7280' }}>{item.sort_order}</Td>
                        <Td>
                          <ToggleLabel title={item.active ? t('admin.bubbleTeaOptions.deactivateTooltip') : t('admin.bubbleTeaOptions.activateTooltip')}>
                            <input
                              type="checkbox"
                              checked={item.active}
                              disabled={toggleMutation.isPending || deleteCategoryMutation.isPending || categoryDeleteRefetchPending}
                              onChange={() => toggleMutation.mutate(item.id)}
                            />
                            <span />
                          </ToggleLabel>
                        </Td>
                        <Td>
                          <ActionCell>
                            <IconButton
                              variant="edit"
                              title={t('admin.bubbleTeaOptions.editTooltip')}
                              disabled={deleteCategoryMutation.isPending || categoryDeleteRefetchPending}
                              onClick={() => openEdit(item)}
                            >
                              <FiEdit2 size={14} />
                            </IconButton>
                            <IconButton
                              variant="delete"
                              title={t('admin.bubbleTeaOptions.deleteTooltip')}
                              disabled={deleteMutation.isPending || deleteCategoryMutation.isPending || categoryDeleteRefetchPending}
                              onClick={() => handleDelete(item.id)}
                            >
                              <FiTrash2 size={14} />
                            </IconButton>
                            <IconButton
                              title={t('admin.bubbleTeaOptions.applyToCategoryTooltip')}
                              disabled={deleteCategoryMutation.isPending || categoryDeleteRefetchPending}
                              onClick={() => openApplyModal(item)}
                            >
                              <FiShare2 size={14} />
                            </IconButton>
                          </ActionCell>
                        </Td>
                      </Tr>
                    ))
                )}
              </tbody>
            </Table>
          </TableWrapper>
        </SectionBody>
      </Section>

      {deleteCategoryError && (
        <ErrorBanner>
          <span>{deleteCategoryError}</span>
          <ErrorDismissButton
            type="button"
            aria-label={t('admin.bubbleTeaOptions.dismissNoticeLabel')}
            onClick={() => {
              setDeleteCategoryError(null);
              setCategoryDeleteRefetchPending(false);
            }}
          >
            ×
          </ErrorDismissButton>
        </ErrorBanner>
      )}

      {itemDeleteError && (
        <ErrorBanner>
          <span>{itemDeleteError}</span>
          <ErrorDismissButton
            type="button"
            aria-label={t('admin.bubbleTeaOptions.dismissNoticeLabel')}
            onClick={() => setItemDeleteError(null)}
          >
            ×
          </ErrorDismissButton>
        </ErrorBanner>
      )}

      {newItemNotice && (
        <InfoMessage>
          <span>{t('admin.bubbleTeaOptions.newItemZeroAvailabilityNotice', { name: newItemNotice, label })}</span>
          <InfoDismissButton
            type="button"
            aria-label={t('admin.bubbleTeaOptions.dismissNoticeLabel')}
            onClick={() => setNewItemNotice(null)}
          >
            ×
          </InfoDismissButton>
        </InfoMessage>
      )}

      {/* task-032 fix_review MEDIUM finding: combo_qr_warning is a plain
          server-generated English string, rendered verbatim (not i18n-keyed
          -- see comboQrWarning's declaration above for rationale). */}
      {comboQrWarning && (
        <InfoMessage>
          <span>{comboQrWarning}</span>
          <InfoDismissButton
            type="button"
            aria-label={t('admin.bubbleTeaOptions.dismissNoticeLabel')}
            onClick={() => setComboQrWarning(null)}
          >
            ×
          </InfoDismissButton>
        </InfoMessage>
      )}

      {/* Add / Edit modal */}
      <Modal isOpen={modalOpen}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>
              {editingItem
                ? t('admin.bubbleTeaOptions.modalEditItemTitle', { name: editingItem.name })
                : t('admin.bubbleTeaOptions.modalAddItemTitle', { label: label.replace(/s$/, '') })}
            </ModalTitle>
            <CloseButton onClick={closeModal}>×</CloseButton>
          </ModalHeader>

          <Form onSubmit={handleSubmit}>
            <FormRow>
              <FormGroup>
                <Label>{t('admin.bubbleTeaOptions.nameLabel')}</Label>
                <Input
                  type="text"
                  value={form.name}
                  onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder={t(`admin.bubbleTeaOptions.namePlaceholders.${type}`, { defaultValue: category.name })}
                  required
                  autoFocus
                />
              </FormGroup>

              <FormGroup>
                <Label>{t('admin.bubbleTeaOptions.nameZhLabel')}</Label>
                <Input
                  type="text"
                  value={form.name_zh}
                  onChange={e => setForm(prev => ({ ...prev, name_zh: e.target.value }))}
                />
              </FormGroup>
            </FormRow>

            {showPrice && (
              <FormGroup>
                <Label>{t('admin.bubbleTeaOptions.extraPriceLabel')}</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.extra_price}
                  onChange={e =>
                    setForm(prev => ({ ...prev, extra_price: parseFloat(e.target.value) || 0 }))
                  }
                />
              </FormGroup>
            )}

            <FormGroup>
              <Label>{t('admin.bubbleTeaOptions.sortOrderLabel')}</Label>
              <Input
                type="number"
                min="0"
                value={form.sort_order}
                onChange={e =>
                  setForm(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))
                }
              />
            </FormGroup>

            <FormGroup>
              <Label>{t('admin.bubbleTeaOptions.optionCodeLabel')}</Label>
              <Input
                type="text"
                data-testid="option-item-code"
                value={form.option_code}
                onChange={e => setForm(prev => ({ ...prev, option_code: e.target.value }))}
                placeholder={t('admin.bubbleTeaOptions.optionCodePlaceholder')}
              />
              <small style={{ color: '#6b7280' }}>{t('admin.bubbleTeaOptions.optionCodeHelp')}</small>
            </FormGroup>

            <FormGroup>
              <CheckboxLabel>
                <input
                  type="checkbox"
                  checked={form.is_default}
                  onChange={e => setForm(prev => ({ ...prev, is_default: e.target.checked }))}
                />
                {t('admin.bubbleTeaOptions.defaultCheckboxLabel')}
              </CheckboxLabel>
            </FormGroup>

            <FormGroup>
              <CheckboxLabel>
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={e => setForm(prev => ({ ...prev, active: e.target.checked }))}
                />
                {t('admin.bubbleTeaOptions.activeCheckboxLabel')}
              </CheckboxLabel>
            </FormGroup>

            {category.triggers_qr_variant && (
              <FormGroup>
                <CheckboxLabel>
                  <input
                    type="checkbox"
                    data-testid="option-item-combo-qr-unavailable"
                    checked={form.combo_qr_unavailable}
                    onChange={e => setForm(prev => ({ ...prev, combo_qr_unavailable: e.target.checked }))}
                  />
                  {t('admin.bubbleTeaOptions.comboQrUnavailableLabel')}
                </CheckboxLabel>
              </FormGroup>
            )}

            {(createStale || updateStale) && (
              <ErrorMessage>
                {(createStale && getErrorDetail(createMutation.error)) ||
                  (updateStale && getErrorDetail(updateMutation.error)) ||
                  t('admin.bubbleTeaOptions.genericError')}
              </ErrorMessage>
            )}

            <ModalActions>
              <CancelButton type="button" onClick={closeModal}>
                {t('admin.bubbleTeaOptions.cancelButton')}
              </CancelButton>
              <SubmitButton type="submit" disabled={isMutating}>
                {isMutating
                  ? (editingItem ? t('admin.bubbleTeaOptions.savingButton') : t('admin.bubbleTeaOptions.addingButton'))
                  : (editingItem ? t('admin.bubbleTeaOptions.saveChangesButton') : t('admin.bubbleTeaOptions.addItemButton'))}
              </SubmitButton>
            </ModalActions>
          </Form>
        </ModalContent>
      </Modal>

      {/* Edit Category modal — categories are a fixed set of 5 option types
          (seeded on the backend), so this is edit-only: no "Add Category".
          Unlike the Add/Edit Item modal above, its content is only mounted
          while open (rather than always-mounted + CSS-hidden) so that its
          "Name"/"Cancel"/"Save Changes" text doesn't collide with the item
          modal's identical labels when both are queried unscoped (e.g. by
          getByText in tests, which — unlike Playwright's `:visible` filter
          used for the item modal in j37 — does not consider CSS display). */}
      <Modal isOpen={categoryModalOpen}>
        {categoryModalOpen && (
          <ModalContent>
            <ModalHeader>
              <ModalTitle>{t('admin.bubbleTeaOptions.modalEditCategoryTitle', { label })}</ModalTitle>
              <CloseButton onClick={closeCategoryModal}>×</CloseButton>
            </ModalHeader>

            <Form onSubmit={handleCategorySubmit}>
              <FormRow>
                <FormGroup>
                  <Label>{t('admin.bubbleTeaOptions.nameLabel')}</Label>
                  <Input
                    type="text"
                    value={categoryForm.name}
                    onChange={e => setCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                    required
                    autoFocus
                  />
                </FormGroup>

                <FormGroup>
                  <Label>{t('admin.bubbleTeaOptions.nameZhLabel')}</Label>
                  <Input
                    type="text"
                    value={categoryForm.name_zh}
                    onChange={e => setCategoryForm(prev => ({ ...prev, name_zh: e.target.value }))}
                  />
                </FormGroup>
              </FormRow>

              <FormGroup>
                <CheckboxLabel>
                  <input
                    type="checkbox"
                    checked={categoryForm.triggers_qr_variant}
                    disabled={editTriggersQrVariantDisabled}
                    onChange={e =>
                      setCategoryForm(prev => ({ ...prev, triggers_qr_variant: e.target.checked }))
                    }
                  />
                  {t('admin.bubbleTeaOptions.triggersQrVariantCheckboxLabel')}
                </CheckboxLabel>
                {editTriggersQrVariantDisabled && (
                  <p style={{ fontSize: 12, color: '#6b7280', margin: '4px 0 0' }}>
                    {t('admin.bubbleTeaOptions.triggersQrVariantDisabledHint')}
                  </p>
                )}
              </FormGroup>

              {updateCategoryMutation.isError && (
                <ErrorMessage>
                  {getErrorDetail(updateCategoryMutation.error) || t('admin.bubbleTeaOptions.genericError')}
                </ErrorMessage>
              )}

              <ModalActions>
                <CancelButton type="button" onClick={closeCategoryModal}>
                  {t('admin.bubbleTeaOptions.cancelButton')}
                </CancelButton>
                <SubmitButton type="submit" disabled={updateCategoryMutation.isPending}>
                  {updateCategoryMutation.isPending ? t('admin.bubbleTeaOptions.savingButton') : t('admin.bubbleTeaOptions.saveChangesButton')}
                </SubmitButton>
              </ModalActions>
            </Form>
          </ModalContent>
        )}
      </Modal>

      {/* Apply-to-category modal (task-028-s6-1): bulk enable/disable this one
          option item across every product sharing a chosen menu_category.
          Wraps the existing bulkApplyOptionToCategory client function; the
          menu_category picker is populated from the qb_categories setting
          (fetched above), never from BubbleTeaOptionCategory. Only mounted
          while open, like the Edit Category modal above. */}
      <Modal isOpen={applyItem !== null}>
        {applyItem && (
          <ModalContent>
            <ModalHeader>
              <ModalTitle>{t('admin.bubbleTeaOptions.applyToCategoryModalTitle')}</ModalTitle>
              <CloseButton onClick={closeApplyModal} disabled={applyMutation.isPending}>×</CloseButton>
            </ModalHeader>

            <p style={{ fontSize: 14, color: '#6b7280', marginTop: -12, marginBottom: 16 }}>
              {t('admin.bubbleTeaOptions.applyToCategoryModalDescription', { name: applyItem.name })}
            </p>

            <Form onSubmit={handleApplySubmit}>
              <FormGroup>
                <Label htmlFor="apply-to-category-menu-category">
                  {t('admin.bubbleTeaOptions.applyMenuCategoryLabel')}
                </Label>
                <Select
                  id="apply-to-category-menu-category"
                  value={applyForm.menu_category}
                  onChange={e => setApplyForm(prev => ({ ...prev, menu_category: e.target.value }))}
                  disabled={qbCategoriesLoading || qbCategoriesError}
                >
                  <option value="">
                    {qbCategoriesLoading
                      ? t('admin.bubbleTeaOptions.applyMenuCategoryLoading')
                      : t('admin.bubbleTeaOptions.applyMenuCategoryPlaceholder')}
                  </option>
                  {qbCategories.map(c => (
                    <option key={c.name} value={c.name}>{c.name}</option>
                  ))}
                </Select>
              </FormGroup>

              {qbCategoriesError && (
                <ErrorMessage>{t('admin.bubbleTeaOptions.applyMenuCategoryLoadError')}</ErrorMessage>
              )}

              <FormGroup>
                <RadioGroup>
                  <RadioLabel>
                    <input
                      type="radio"
                      name="apply-to-category-availability"
                      checked={applyForm.available}
                      onChange={() => setApplyForm(prev => ({ ...prev, available: true }))}
                    />
                    {t('admin.bubbleTeaOptions.applyEnableOption')}
                  </RadioLabel>
                  <RadioLabel>
                    <input
                      type="radio"
                      name="apply-to-category-availability"
                      checked={!applyForm.available}
                      onChange={() => setApplyForm(prev => ({ ...prev, available: false }))}
                    />
                    {t('admin.bubbleTeaOptions.applyDisableOption')}
                  </RadioLabel>
                </RadioGroup>
              </FormGroup>

              {applyMutation.isError && applyIsCurrentSession && (
                <ErrorMessage>
                  {getErrorDetail(applyMutation.error) || t('admin.bubbleTeaOptions.applyError')}
                </ErrorMessage>
              )}

              {applyMutation.isSuccess && applyIsCurrentSession && (
                <SuccessMessage>
                  {applyMutation.data.affected_count === 1
                    ? t('admin.bubbleTeaOptions.applySuccessCountSingular', { count: applyMutation.data.affected_count })
                    : t('admin.bubbleTeaOptions.applySuccessCountPlural', { count: applyMutation.data.affected_count })}
                </SuccessMessage>
              )}

              <ModalActions>
                <CancelButton type="button" onClick={closeApplyModal} disabled={applyMutation.isPending}>
                  {t('admin.bubbleTeaOptions.cancelButton')}
                </CancelButton>
                <SubmitButton
                  type="submit"
                  disabled={applyMutation.isPending || qbCategoriesLoading || qbCategoriesError || !applyForm.menu_category}
                >
                  {applyMutation.isPending
                    ? t('admin.bubbleTeaOptions.applyingButton')
                    : t('admin.bubbleTeaOptions.applySubmitButton')}
                </SubmitButton>
              </ModalActions>
            </Form>
          </ModalContent>
        )}
      </Modal>
    </>
  );
};

// ─── Main page component ──────────────────────────────────────────────────────

const AdminBubbleTeaOptions: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: rawCategories, isLoading, isError } = useQuery({
    queryKey: ['bubbleTeaOptionCategories'],
    queryFn: fetchBubbleTeaOptionCategories,
  });
  const categories = Array.isArray(rawCategories) ? rawCategories : [];

  const [addCategoryModalOpen, setAddCategoryModalOpen] = useState(false);
  const [addCategoryForm, setAddCategoryForm] = useState<AddCategoryFormState>(BLANK_ADD_CATEGORY_FORM);
  const [newCategoryNotice, setNewCategoryNotice] = useState<string | null>(null);

  const createCategoryMutation = useMutation({
    mutationFn: (data: AddCategoryFormState) => createOptionCategory(data),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['bubbleTeaOptionCategories'] });
      closeAddCategoryModal();
      setNewCategoryNotice(created.name);
    },
  });

  const openAddCategoryModal = () => {
    setAddCategoryForm(BLANK_ADD_CATEGORY_FORM);
    setAddCategoryModalOpen(true);
  };

  const closeAddCategoryModal = () => {
    setAddCategoryModalOpen(false);
    setAddCategoryForm(BLANK_ADD_CATEGORY_FORM);
    createCategoryMutation.reset();
  };

  const handleAddCategorySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createCategoryMutation.mutate(addCategoryForm);
  };

  if (isLoading) {
    return (
      <LoadingContainer>
        <Spinner />
      </LoadingContainer>
    );
  }

  // Only show the full-page load error when we have no data at all; a background
  // refetch failure (e.g. after category delete) must not unmount the already-loaded
  // list — see the categoryDeleteRefetchError banner in OptionSection.
  if (isError && rawCategories === undefined) {
    return (
      <Container>
        <Header>
          <Title>{t('admin.bubbleTeaOptions.title')}</Title>
        </Header>
        <ErrorMessage>{t('admin.bubbleTeaOptions.loadError')}</ErrorMessage>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>{t('admin.bubbleTeaOptions.title')}</Title>
        <Button onClick={openAddCategoryModal}>
          <FiPlus size={14} />
          {t('admin.bubbleTeaOptions.modalAddCategoryTitle')}
        </Button>
      </Header>

      {newCategoryNotice && (
        <InfoMessage>
          <span>{t('admin.bubbleTeaOptions.newCategoryZeroAvailabilityNotice', { name: newCategoryNotice })}</span>
          <InfoDismissButton
            type="button"
            aria-label={t('admin.bubbleTeaOptions.dismissNoticeLabel')}
            onClick={() => setNewCategoryNotice(null)}
          >
            ×
          </InfoDismissButton>
        </InfoMessage>
      )}

      {/* Dynamic iteration over whatever categories the API returns, sorted
          by sort_order (task-028-s5-1) -- replaces the old fixed 5-type
          SECTION_CONFIG so newly-created categories render immediately with
          no code change. */}
      {[...categories]
        .sort((a, b) => a.sort_order - b.sort_order)
        .map(category => (
          <OptionSection key={category.id} category={category} />
        ))}

      {/* Add Category modal (task-028-s5-1) -- lets admins create brand-new
          option categories beyond the original 5 pre-seeded ones. */}
      <Modal isOpen={addCategoryModalOpen}>
        {addCategoryModalOpen && (
          <ModalContent>
            <ModalHeader>
              <ModalTitle>{t('admin.bubbleTeaOptions.modalAddCategoryTitle')}</ModalTitle>
              <CloseButton onClick={closeAddCategoryModal}>×</CloseButton>
            </ModalHeader>

            <Form onSubmit={handleAddCategorySubmit}>
              <FormRow>
                <FormGroup>
                  <Label>{t('admin.bubbleTeaOptions.nameLabel')}</Label>
                  <Input
                    type="text"
                    value={addCategoryForm.name}
                    onChange={e => setAddCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                    required
                    autoFocus
                  />
                </FormGroup>

                <FormGroup>
                  <Label>{t('admin.bubbleTeaOptions.nameZhLabel')}</Label>
                  <Input
                    type="text"
                    value={addCategoryForm.name_zh}
                    onChange={e => setAddCategoryForm(prev => ({ ...prev, name_zh: e.target.value }))}
                  />
                </FormGroup>
              </FormRow>

              <FormGroup>
                <Label>{t('admin.bubbleTeaOptions.optionTypeLabel')}</Label>
                <Input
                  type="text"
                  value={addCategoryForm.option_type}
                  onChange={e => setAddCategoryForm(prev => ({ ...prev, option_type: e.target.value }))}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>{t('admin.bubbleTeaOptions.selectionModeLabel')}</Label>
                <RadioGroup>
                  <RadioLabel>
                    <input
                      type="radio"
                      name="add-category-selection-mode"
                      checked={addCategoryForm.selection_mode === 'single'}
                      onChange={() => setAddCategoryForm(prev => ({ ...prev, selection_mode: 'single' }))}
                    />
                    {t('admin.bubbleTeaOptions.selectionModeSingleOption')}
                  </RadioLabel>
                  <RadioLabel>
                    <input
                      type="radio"
                      name="add-category-selection-mode"
                      checked={addCategoryForm.selection_mode === 'multiple'}
                      onChange={() =>
                        // Switching to 'multiple' also forces triggers_qr_variant back to
                        // false (task-032-impl-6) -- the checkbox below is about to become
                        // disabled, and leaving a stale `true` in state would otherwise be
                        // silently submitted and rejected by the backend's 422 check.
                        setAddCategoryForm(prev => ({ ...prev, selection_mode: 'multiple', triggers_qr_variant: false }))
                      }
                    />
                    {t('admin.bubbleTeaOptions.selectionModeMultipleOption')}
                  </RadioLabel>
                </RadioGroup>
              </FormGroup>

              <FormGroup>
                <CheckboxLabel>
                  <input
                    type="checkbox"
                    checked={addCategoryForm.triggers_qr_variant}
                    disabled={addCategoryForm.selection_mode === 'multiple'}
                    onChange={e =>
                      setAddCategoryForm(prev => ({ ...prev, triggers_qr_variant: e.target.checked }))
                    }
                  />
                  {t('admin.bubbleTeaOptions.triggersQrVariantCheckboxLabel')}
                </CheckboxLabel>
                {addCategoryForm.selection_mode === 'multiple' && (
                  <p style={{ fontSize: 12, color: '#6b7280', margin: '4px 0 0' }}>
                    {t('admin.bubbleTeaOptions.triggersQrVariantDisabledHint')}
                  </p>
                )}
              </FormGroup>

              <FormGroup>
                <Label>{t('admin.bubbleTeaOptions.sortOrderLabel')}</Label>
                <Input
                  type="number"
                  min="0"
                  value={addCategoryForm.sort_order}
                  onChange={e =>
                    setAddCategoryForm(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))
                  }
                />
              </FormGroup>

              {createCategoryMutation.isError && (
                <ErrorMessage>
                  {getErrorDetail(createCategoryMutation.error) || t('admin.bubbleTeaOptions.genericError')}
                </ErrorMessage>
              )}

              <ModalActions>
                <CancelButton type="button" onClick={closeAddCategoryModal}>
                  {t('admin.bubbleTeaOptions.cancelButton')}
                </CancelButton>
                <SubmitButton type="submit" disabled={createCategoryMutation.isPending}>
                  {createCategoryMutation.isPending
                    ? t('admin.bubbleTeaOptions.addingButton')
                    : t('admin.bubbleTeaOptions.addSubmitCategoryButton')}
                </SubmitButton>
              </ModalActions>
            </Form>
          </ModalContent>
        )}
      </Modal>
    </Container>
  );
};

export default AdminBubbleTeaOptions;
